/**
 * Reusable active-shape sector that animates a bump out (and in for donuts)
 * for pie/donut hover. Use as Recharts Pie activeShape.
 */
import { useEffect, useState } from 'react';
import { Sector } from 'recharts';

export const BUMP_PX = 6;
export const BUMP_ANIMATION_MS = 200;

function easeOutQuad(t: number): number {
    return 1 - (1 - t) * (1 - t);
}

export interface AnimatedBumpSectorProps
    extends React.ComponentProps<typeof Sector> {
    variant: 'pie' | 'donut';
    disableAnimation?: boolean;
}

export function AnimatedBumpSector({
    variant,
    disableAnimation,
    ...props
}: AnimatedBumpSectorProps) {
    const hasNumericRadii =
        typeof props.outerRadius === 'number' &&
        (variant === 'pie' || typeof props.innerRadius === 'number');
    const baseOuter =
        typeof props.outerRadius === 'number' ? props.outerRadius : 0;
    const baseInner =
        typeof props.innerRadius === 'number' ? props.innerRadius : 0;
    const targetOuter = baseOuter + BUMP_PX;
    const targetInner =
        variant === 'donut' ? Math.max(0, baseInner - BUMP_PX) : baseInner;

    const [outer, setOuter] = useState(baseOuter);
    const [inner, setInner] = useState(baseInner);

    useEffect(() => {
        if (!hasNumericRadii || disableAnimation) return;
        let raf = 0;
        const start = performance.now();
        const tick = (now: number) => {
            const t = Math.min((now - start) / BUMP_ANIMATION_MS, 1);
            const e = easeOutQuad(t);
            setOuter(baseOuter + (targetOuter - baseOuter) * e);
            if (variant === 'donut') {
                setInner(baseInner + (targetInner - baseInner) * e);
            }
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [
        hasNumericRadii,
        disableAnimation,
        baseOuter,
        baseInner,
        targetOuter,
        targetInner,
        variant,
    ]);

    if (!hasNumericRadii || disableAnimation) {
        return (
            <Sector
                {...props}
                outerRadius={
                    typeof props.outerRadius === 'number'
                        ? props.outerRadius + BUMP_PX
                        : props.outerRadius
                }
                innerRadius={
                    variant === 'donut' && typeof props.innerRadius === 'number'
                        ? Math.max(0, props.innerRadius - BUMP_PX)
                        : props.innerRadius
                }
            />
        );
    }

    return (
        <Sector
            {...props}
            outerRadius={outer}
            innerRadius={variant === 'donut' ? inner : props.innerRadius}
        />
    );
}
